import { useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  auctionApi,
  Bid,
  BidStatus,
  PublicAuctionWithTribe,
} from 'src/api/auctionApi';

import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import styles from './Auction.module.scss';
import { AuctionInfo } from './AuctionInfo/AuctionInfo';

import { Circle } from 'src/api/circles';
import { TabsAuction } from './Tabs/Tabs';

const fetchUserBids = async (auctionId: string): Promise<Bid | null> => {
  try {
    const userBids = await auctionApi.getUserBids(auctionId);

    // Single user can only have a single bid per auction
    return Array.isArray(userBids) && userBids.length ? userBids[0] : null;
  } catch (error) {
    console.error('Error fetching bid', error);
    throw error;
  }
};

const updateBidStatus = async (
  bidId: string,
  bidStatus: BidStatus
): Promise<Bid> => {
  try {
    const updatedBid = await auctionApi.updateBidStatus(bidId, bidStatus);
    return updatedBid;
  } catch (error) {
    console.error('Error updating bid status', error);
    throw error;
  }
};

export const Auction = () => {
  // TODO rename route param to :auctionId
  const { id: auctionId } = useParams();

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const stripeProcessingStatus = 'PROCESSING';
  const stripeStatus = useMemo(
    () => searchParams.get('stripe_status'),
    [searchParams]
  );
  const [shouldClearQueryParam, setShouldClearQueryParam] = useState(false);

  const [auctionData, setAuctionEntity] =
    useState<PublicAuctionWithTribe | null>(null);
  const [circleData, setCircleEntity] = useState<Circle | null>(null);
  const [bidData, setBidEntity] = useState<Bid | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  useEffect(() => {
    if (!auctionId) {
      return;
    }

    const fetchPublicAuction = async () => {
      try {
        const publicAuction = await auctionApi.getPublicAuctionData(auctionId);
        setAuctionEntity(publicAuction);
        setCircleEntity(publicAuction.tribe);
      } catch (error) {
        console.error('Error fetching auction', error);
      }
    };

    fetchPublicAuction();
  }, [auctionId, refreshTrigger]);

  useEffect(() => {
    const fetchBid = async () => {
      if (!auctionData) {
        return;
      }

      try {
        let bid = await fetchUserBids(auctionData._id);
        if (!bid) {
          setBidEntity(null);
          return;
        }

        if (stripeStatus === stripeProcessingStatus) {
          bid = await updateBidStatus(bid._id, BidStatus.PROCESSING);

          // NOTE: if there are issues can turn this into setInterval
          // and poll the stripeSession until null
          if (bid.stripeSession) {
            setTimeout(() => setShouldClearQueryParam(true), 5000);
          } else {
            setShouldClearQueryParam(true);
          }
        }

        setBidEntity(bid);
      } catch (error) {
        console.error('Error fetching the bid');
      }
    };

    fetchBid();
  }, [auctionData, stripeStatus]);

  useEffect(() => {
    if (shouldClearQueryParam) {
      navigate(location.pathname, { replace: true });
      setShouldClearQueryParam(false);
    }
  }, [shouldClearQueryParam, location.pathname, navigate]);

  const handleRefreshAuctionData = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <div className={styles.container}>
      {auctionData ? (
        <>
          <AuctionInfo
            auction={auctionData!}
            circle={circleData!}
            userBidData={bidData}
            onRefreshAuctionData={handleRefreshAuctionData}
          />
          <TabsAuction circleData={circleData} />
        </>
      ) : (
        <MaterialCircularProgress />
      )}
    </div>
  );
};
